import { AddShoppingCart, Celebration } from '@mui/icons-material';
import { Alert, AlertTitle, Chip, Link, List, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FlashcardDeckItem from '@/components/flashcard/flashcard-deck-item';
import FlashcardStats from '@/components/flashcard/flashcard-stats';
import LoadingIndicator from '@/components/shared/loading-indicator';
import { useFlashcardDecksAndStats } from '@/hooks';
import { useCourses } from '@/providers';
import { useRoute } from '@/utils/client/route';

export default function FlashcardDecksTab() {
  const { t } = useTranslation();
  const { goToRoute } = useRoute();
  const { selectedCourse, isLoading: courseStatusLoading, courseStatus } = useCourses();
  const { isLoading: decksLoading, data: decksAndStats } = useFlashcardDecksAndStats(
    selectedCourse?.id,
  );

  if (!selectedCourse) return null;

  const { slug: courseSlug, testAt } = selectedCourse;
  const hasMembership = courseStatus?.hasMembership;

  const onNavigate = (deckSlug: string) => {
    goToRoute('myCourseFlashcards', {
      courseSlug,
      deckSlug,
    });
  };

  if (!courseStatus?.plannedToFinishAt) {
    return (
      <Paper>
        <Alert severity="warning">
          {t('flashcard.tab.planningMissingPrefix')}{' '}
          <Link href="./ergebnisse/">{t('flashcard.tab.planningMissingLink')}</Link>
          {t('flashcard.tab.planningMissingSuffix')}
        </Alert>
      </Paper>
    );
  }

  if (decksLoading || courseStatusLoading) {
    return (
      <Paper>
        <LoadingIndicator />
      </Paper>
    );
  }

  if (!decksAndStats) {
    return (
      <Paper>
        <Alert severity="error">{t('flashcard.tab.loadError')}</Alert>
      </Paper>
    );
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }} icon={<Celebration />}>
        <AlertTitle>
          {t('flashcard.tab.infoTitle')}
          <Chip
            label={t('flashcard.tab.beta')}
            size="small"
            variant="outlined"
            color="info"
            sx={{ mx: 1 }}
          />{' '}
        </AlertTitle>
        {t('flashcard.tab.infoBody1')}{' '}
        <Link href="mailto:info@mymorize.com">info@mymorize.com</Link>.<br />
        <br />
        {t('flashcard.tab.infoBody2')}
      </Alert>
      <FlashcardStats
        testAt={testAt}
        decksAndStats={decksAndStats}
        plannedToFinishAt={courseStatus?.plannedToFinishAt}
      />
      {!hasMembership && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<AddShoppingCart />}>
          {t('flashcard.tab.freeTrialPrefix')}{' '}
          <Chip
            label={t('flashcard.tab.freeTrialChip')}
            size="small"
            variant="outlined"
            color="success"
            sx={{ mx: 1 }}
          />{' '}
          {t('flashcard.tab.freeTrialSuffix')}
        </Alert>
      )}
      <List component="nav" aria-label={t('flashcard.tab.listAria')}>
        {decksAndStats.decks.map((item) => (
          <FlashcardDeckItem
            key={item.deck.slug}
            item={item}
            hasMembership={!!hasMembership}
            onNavigate={onNavigate}
          />
        ))}
      </List>
    </>
  );
}
